import { Icon } from '../../display/Icon';
import { Tooltip } from '../../display/Tooltip';
import { TextInput } from '../../forms/TextInput';
import type { TextInputProps } from '../../forms/TextInput';
import { useCopyToClipboard } from '../../../hooks';

import styles from './CopyTextInput.module.scss';

type CopyTextInputProps = Omit<
  TextInputProps,
  'isMultiline' | 'isFullWidth' | 'renderIconAfter'
> & {
  /** Same value as TextInputProps and it is required */
  value: string;
};

/**
 * `CopyTextInput` renders a readonly `TextInput` with a clickable `Icon` in it, which is used to copy the text within the input.
 */
export const CopyTextInput = ({
  size = 'medium',
  type = 'text',
  value,
  ...otherProps
}: CopyTextInputProps) => {
  const props = {
    size,
    type,
    value,
    ...otherProps,
  } satisfies CopyTextInputProps;

  const [didCopy, handleCopyText] = useCopyToClipboard(value);
  const onClickCopy = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    handleCopyText();
  };
  const renderCopyButton = ({ iconSize }: { iconSize: number }) => {
    const ariaLabel = didCopy
      ? `"${value}" copied to the clipboard`
      : `Copy "${value}" to the clipboard`;
    return (
      <button
        aria-hidden={!value}
        aria-label={ariaLabel}
        className={styles.copyButton}
        color="primary"
        disabled={!value}
        onClick={onClickCopy}
        type="button"
        data-zds
      >
        <Icon isBlock={true} name="actionCopy" size={iconSize} />
        <div className={styles.tooltip} data-hidden={!didCopy} data-zds>
          <Tooltip aria-hidden={true} position="southeast">
            Copied to clipboard!
          </Tooltip>
        </div>
      </button>
    );
  };
  return (
    <div className={styles.root} data-zds>
      <TextInput
        {...props}
        aria-hidden={true}
        disabledText={value ? '' : 'Disabled'}
        isDisabled={!value}
        isReadonly={Boolean(value)}
        isFocusable={false}
        renderIconAfter={renderCopyButton}
      />
    </div>
  );
};
